// @flow

import React from 'react'
import dayjs from 'dayjs'
import { Link } from 'gatsby'
import { markdown } from 'markdown'
import Container from './Container'
import Appear from './Appear'
import SectionTitle from './SectionTitle'
import Cta from './Cta'
import Image from './Image'

type SessionType = {
  title: string,
  description: string,
  sessionNumber: string,
  schedule: string,
  date: string,
  session: string,
}

type VideoType = {
  videoTitle: string,
  videoPoster: any,
  videoSlug: string,
}
type Props = {
  title: string,
  session: SessionType,
  children: any,
  watch_video: string,
  videos: Array<VideoType>,
}

const ConferenceImage = ({ className, color }) => (
  <svg className={className} viewBox="0 0 119.57 121.68" xmlns="http://www.w3.org/2000/svg"><g fill="none" stroke={color} strokeMiterlimit="10" strokeWidth="4"><path d="m12.76 64.7c-2.88 0-5.23-1.76-5.23-3.92v-54.86c0-2.16 2.35-3.92 5.23-3.92h94.05c2.87 0 5.19 1.76 5.19 3.92v54.86c0 2.16-2.35 3.92-5.22 3.92z"/><circle cx="59.78" cy="21.16" r="8.85"/><path d="m43.8 54.45v-12.45s.08-8.68 8.68-8.68h14.61s8.68 0 8.68 8.68v12.45"/><circle cx="59.78" cy="88.39" r="8.85"/><path d="m43.8 121.68v-12.42s.08-8.68 8.68-8.68h14.61s8.68 0 8.68 8.68v12.42"/><circle cx="17.98" cy="88.39" r="8.85"/><path d="m2 121.68v-12.42s.08-8.68 8.68-8.68h14.61s8.68 0 8.68 8.68v12.42"/><circle cx="101.58" cy="88.39" r="8.85"/><path d="m85.6 121.68v-12.42s.08-8.68 8.68-8.68h14.6s8.69 0 8.69 8.68v12.42"/></g></svg>
)

const Session = ({
  title,
  session,
  children,
  watch_video,
  videos,
}: Props) => {
  const localeDate = dayjs(session.date).format("DD-MM-YYYY")

  const hasVideos = videos.length > 0 || videos.length === 1 && videos[0] !== ""

  return (
    <div className="bg-light-gray">
      <SectionTitle title={title} />
      <Container className="pb-16">
      <div className="flex flex-col h-full shadow-xl">
        <div className="h-4 bg-white border-b-4 border-dashed border-light-gray" />
        <div key="content" className="flex-1 pt-6 pb-12 bg-white md:pb-8">
          <Appear>
            <div
              key="image-container"
              className="flex flex-col items-center justify-center px-5 pb-6 text-center md:text-left md:px-8 md:flex-row md:items-start md:justify-start"
            >
              <span className="block text-xl font-black leading-tight md:hidden" style={{ color: `#${session.color}` }}>{session.session} {session.sessionNumber}</span>
              <div className="my-6 flex-0 md:my-0 md:mr-4">
                <ConferenceImage className="w-20 h-20" color={`#${session.color}`} />
              </div>
              <div className="text-base font-black text-green" style={{ color: `#${session.color}` }}>
                <span className="hidden text-xl leading-tight md:block">{session.session} {session.sessionNumber}</span>
                <span className="block">{session.title}</span>
                <span className="block">{localeDate}</span>
                {session.description && 
                  <span className="markdownNoMargin" dangerouslySetInnerHTML={{ __html: markdown.toHTML(session.description) }} />
                }
              </div>
            </div>
          </Appear>
          <div
            key="content-container"
            className="text-base"
            style={{ color: `#${session.color}` }}
          >
            {children}
          </div>
          {hasVideos && (
            videos.map(video => {
              const {
                videoSlug,
                videoTitle,
                videoPoster,
              } = video

              const shouldRender = (
                typeof videoSlug === "string"
                && videoSlug !== ""
                && typeof videoTitle === "string"
                && videoTitle !== ""
              )

              if (!shouldRender) return null

              return (
                <Appear key={videoSlug}>
                  <Link to={videoSlug} className="block mx-5 mt-12 md:mx-8 md:mt-8">
                    <div className="relative">
                      <Image image={videoPoster} alt={videoTitle} imgClassName="cursor-pointer" />
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
                      >
                        <Cta
                          ctaText={watch_video}
                          color={session.color}
                          isDisabled
                          fullWidth={false}
                        />
                      </div>
                    </div>
                  </Link>
                </Appear>
              )
            })
          )}
        </div>
      </div>
      </Container>
    </div>
  )
}

export default Session
